import {useEffect, useState} from "react";

import {AsyncSearchCombobox} from "@/components/ui/async-search-combobox";
import {Badge} from "@/components/ui/badge";
import {Item, ItemContent, ItemDescription, ItemTitle} from "@/components/ui/item";
import {cn} from "@/lib/utils";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import type {ParamsOptions} from "@/src/modules/core/types";

import {useInfiniteVehicles} from "../hooks/use-infinite-vehicles";
import {
  buildVehicleListFilter,
  isVehicleListScopeOk,
  vehicleListPlaceholder,
} from "../lib/vehicle-list-filter";
import {isResidentVehicle} from "../lib/is-resident-vehicle";
import {
  getVehicleComboboxLabel,
  VEHICLE_SEARCH_DEBOUNCE_MS,
  VEHICLE_SEARCH_MIN_LENGTH,
} from "../lib/vehicle-search";
import type {Vehicle} from "../types";

export {getVehicleComboboxLabel};

type VehiclesComboboxBaseProps = {
  houseHoldId?: number | null;
  /**
   * When `houseHoldId` is null, vehicles are listed for this property (`filter[context_property_id]`).
   * Omit when using logged-in property scope (`filter[property_id]`).
   */
  contextPropertyId?: number | null;
  /**
   * Permit-selection mode (permanent parking). Lists ALL active property vehicles via
   * `filter[for_household_id]` so unknown vehicles (`resident_id = null`) are selectable;
   * the backend adds `belongs_to_other_resident` per row. Requires `contextPropertyId` +
   * `forHouseholdId`. Never sends hard `house_hold_id`.
   */
  permitSelection?: boolean;
  forHouseholdId?: number | null;
  /** Show Resident / Unknown badge on the right. Also implied by `permitSelection`. */
  showResidencyBadge?: boolean;
  disabled?: boolean;
  className?: string;
  excludeVehicleIds?: number[];
  filter?: ParamsOptions["filter"];
  sort?: ParamsOptions["sort"];
};

/** Form selections often only carry id + plate/make/model. */
export type VehiclesComboboxValue = Pick<Vehicle, "vehicle_id"> &
  Partial<Omit<Vehicle, "vehicle_id">>;

type VehiclesComboboxSingleProps = VehiclesComboboxBaseProps & {
  multiple?: false;
  value: Vehicle | VehiclesComboboxValue | null;
  onChange: (value: Vehicle | null) => void;
};

type VehiclesComboboxMultipleProps = VehiclesComboboxBaseProps & {
  multiple: true;
  value: Vehicle[] | VehiclesComboboxValue[];
  onChange: (value: Vehicle[]) => void;
  shouldRenderSelection?: boolean;
};

export type VehiclesComboboxProps = VehiclesComboboxSingleProps | VehiclesComboboxMultipleProps;

function VehicleResidencyBadge({vehicle}: {vehicle: Vehicle}) {
  const isResident = isResidentVehicle(vehicle);
  if (isResident) {
    return (
      <Badge
        variant="info"
        className="shrink-0 px-1.5 py-0 text-[10px] font-medium uppercase tracking-wide"
      >
        Resident
      </Badge>
    );
  }
  return (
    <Badge
      variant="secondary"
      className="shrink-0 border-violet-200 bg-violet-200 px-1.5 py-0 text-[10px] font-medium tracking-wide text-violet-900 uppercase dark:border-violet-500/40 dark:bg-violet-950 dark:text-violet-200"
    >
      Unknown
    </Badge>
  );
}

function normalizeDisplayField(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? "";
  if (!trimmed || trimmed.toLowerCase() === "null") {
    return "";
  }
  if (trimmed === "-" || trimmed === "—") {
    return "";
  }
  return trimmed;
}

function vehicleComboboxDescription(option: Vehicle, blocked: boolean): string | null {
  if (blocked) {
    return "Belongs to another resident";
  }

  const spec = [normalizeDisplayField(option.make), normalizeDisplayField(option.model)]
    .filter(Boolean)
    .join(" ");

  const owner = [
    normalizeDisplayField(option.car_owner_name),
    normalizeDisplayField(option.car_owner_last_name),
  ]
    .filter(Boolean)
    .join(" ");

  const parts = [spec, owner].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function VehicleComboboxItem({
  option,
  showResidencyBadge,
  blocked = false,
}: {
  option: Vehicle;
  showResidencyBadge: boolean;
  blocked?: boolean;
}) {
  const description = vehicleComboboxDescription(option, blocked);

  const body = (
    <ItemContent className="min-w-0 flex-1">
      <ItemTitle className={cn(blocked && "text-muted-foreground")}>{option.plate}</ItemTitle>
      {description ? (
        <ItemDescription className={cn("text-xs", blocked && "text-destructive")}>
          {description}
        </ItemDescription>
      ) : null}
    </ItemContent>
  );

  return (
    <Item size="sm" className={cn("p-0", blocked && "opacity-60")}>
      {showResidencyBadge && !blocked ? (
        <div className="flex w-full items-start justify-between gap-2">
          {body}
          <VehicleResidencyBadge vehicle={option} />
        </div>
      ) : (
        body
      )}
    </Item>
  );
}

export function VehiclesCombobox(props: VehiclesComboboxProps) {
  const {
    houseHoldId,
    contextPropertyId,
    permitSelection = false,
    forHouseholdId = null,
    showResidencyBadge = false,
    disabled,
    className,
    excludeVehicleIds = [],
    filter: extraFilter = {},
    sort: sortProp,
    multiple,
  } = props;

  const userProperty = useStoredUserProperty();
  const propertyId = userProperty?.id ?? null;
  const contextPropertyIdPropDefined = contextPropertyId !== undefined;

  /** Property scope only when no task/permit scope props were passed (stored vehicles, vehicle actions sheet). */
  const usesPropertyScope =
    !permitSelection && contextPropertyId === undefined && houseHoldId === undefined;

  const scopeOk = isVehicleListScopeOk({
    permitSelection,
    forHouseholdId,
    houseHoldId: houseHoldId ?? null,
    contextPropertyId,
    propertyId: usesPropertyScope ? propertyId : null,
    contextPropertyIdPropDefined,
  });

  const defaultSort = usesPropertyScope ? "plate" : "-created_at";
  const sort = sortProp ?? defaultSort;

  const buildFilter = (searchTerm?: string) =>
    buildVehicleListFilter({
      permitSelection,
      forHouseholdId,
      houseHoldId: houseHoldId ?? null,
      contextPropertyId,
      propertyId: usesPropertyScope ? propertyId : null,
      extraFilter,
      searchTerm,
    });

  const [options, setOptions] = useState<ParamsOptions>({
    sort,
    filter: buildFilter(),
  });
  const [activeSearch, setActiveSearch] = useState("");

  const trimmedSearch = activeSearch.trim();
  /** Property-wide lists (`context_property_id` without household) must not load until the user searches. */
  const isPropertyWideSearch =
    !permitSelection &&
    !houseHoldId &&
    contextPropertyId != null &&
    contextPropertyId > 0;
  const searchMeetsMin = trimmedSearch.length >= VEHICLE_SEARCH_MIN_LENGTH;
  const isSearchGated = isPropertyWideSearch && !searchMeetsMin;

  const query = useInfiniteVehicles(options, {
    enabled: scopeOk && !disabled && !isSearchGated,
  });
  const {vehicles, listMeta, hasNextPage, fetchNextPage, isFetching, isFetchingNextPage} = query;

  const visibleVehicles = vehicles.filter((item) => !excludeVehicleIds.includes(item.vehicle_id));

  const isAmbiguous =
    searchMeetsMin &&
    (listMeta?.multiple_plate_prefix_matches === true || (listMeta?.total ?? 0) > 1);

  useEffect(() => {
    setOptions((old) => ({
      ...old,
      sort,
      filter: buildFilter(),
    }));
    setActiveSearch("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [houseHoldId, contextPropertyId, permitSelection, forHouseholdId, propertyId, sort]);

  const handleSearch = (term: string) => {
    setActiveSearch(term);
    const trimmed = term.trim();
    const searchForApi =
      trimmed.length >= VEHICLE_SEARCH_MIN_LENGTH ? trimmed : undefined;
    setOptions((old) => {
      const nextFilter = buildFilter(searchForApi);
      const prevQ = typeof old.filter?.q === "string" ? old.filter.q : "";
      const nextQ = typeof nextFilter.q === "string" ? nextFilter.q : "";
      if (prevQ === nextQ) {
        return old;
      }
      return {...old, filter: nextFilter};
    });
  };

  const isDisabled = disabled || !scopeOk;

  const handleChange = (newValue: Vehicle | Vehicle[] | null) => {
    if (multiple) {
      let next = Array.isArray(newValue) ? newValue : [];
      if (permitSelection) {
        next = next.filter((item) => item.belongs_to_other_resident !== true);
      }
      (props as VehiclesComboboxMultipleProps).onChange(next);
    } else {
      const next = newValue as Vehicle | null;
      if (permitSelection && next?.belongs_to_other_resident === true) {
        return;
      }
      (props as VehiclesComboboxSingleProps).onChange(next);
    }
  };

  const placeholder = isPropertyWideSearch
    ? `Search vehicles (min ${VEHICLE_SEARCH_MIN_LENGTH} characters)`
    : vehicleListPlaceholder(scopeOk, {
        permitSelection,
        contextPropertyId,
        contextPropertyIdPropDefined,
      });

  const renderItem = (option: Vehicle) => {
    const blocked = permitSelection && option.belongs_to_other_resident === true;
    const showBadge = showResidencyBadge || permitSelection;

    return (
      <VehicleComboboxItem
        option={option}
        showResidencyBadge={showBadge}
        blocked={blocked}
      />
    );
  };

  const comboboxItems = isSearchGated ? [] : visibleVehicles;
  const comboboxEmptyMessage = isSearchGated
    ? `Type at least ${VEHICLE_SEARCH_MIN_LENGTH} characters to search vehicles`
    : "No vehicles found";

  const commonProps = {
    items: comboboxItems,
    onSearch: handleSearch,
    onLoadMore: fetchNextPage,
    hasMore: hasNextPage ?? false,
    isLoading: isFetching,
    isLoadingMore: isFetchingNextPage ?? false,
    getItemKey: (item: Vehicle) => item.vehicle_id,
    getItemLabel: getVehicleComboboxLabel,
    isItemEqual: (item: Vehicle, selected: Vehicle | null) =>
      item.vehicle_id === selected?.vehicle_id,
    placeholder,
    debounceMs: VEHICLE_SEARCH_DEBOUNCE_MS,
    loadingMessage: "Fetching vehicles",
    loadingMoreMessage: "Fetching more vehicles",
    disabled: isDisabled,
    className,
    emptyMessage: comboboxEmptyMessage,
    allowKeyboardAutoSelect: !isAmbiguous,
    renderItem,
  };

  const ambiguityHelper = isAmbiguous ? (
    <p className="text-muted-foreground text-xs">Multiple vehicles match — select one.</p>
  ) : null;

  if (multiple) {
    const m = props as VehiclesComboboxMultipleProps;
    return (
      <div className="flex flex-col gap-1">
        <AsyncSearchCombobox<Vehicle>
          {...commonProps}
          grouped={false as const}
          shouldRenderSelection={m.shouldRenderSelection ?? true}
          multiple
          value={m.value as Vehicle[]}
          onChange={handleChange}
        />
        {ambiguityHelper}
      </div>
    );
  }

  const s = props as VehiclesComboboxSingleProps;
  return (
    <div className="flex flex-col gap-1">
      <AsyncSearchCombobox<Vehicle>
        {...commonProps}
        grouped={false as const}
        multiple={false}
        value={s.value as Vehicle | null}
        onChange={handleChange}
      />
      {ambiguityHelper}
    </div>
  );
}
